import {MediaVideoTeaser} from "../media/media--video";
import {Dictionary, DrupalSearchApiResponseMeta, DrupalVideoMedia, FacetFilters} from "@/types/hygiena-types";
import {Fragment, useContext, useEffect, useRef, useState} from "react";
import {Pagination} from "@/components/organisms/widget--pagination";
import {LoadingSpinnerOverlay} from "@/components/atoms/loading-spinner";
import {videoSearch} from "@/lib/video-search";
import {useRouter} from "next/router";
import {DocumentSearchFacet} from "@/components/organisms/widget--document-search";
import {FilterResetButton} from "@/components/atoms/reset-button";
import {DrupalSearchApiFacet} from "next-drupal";
import classNames from "classnames";
import {DictionaryContext} from "@/context/dictionary-context";
import {Body} from "@/components/atoms/body";
import {Link} from "@/components/atoms/link";

export function ParagraphSolrVideoGrid({paragraph, ...props}) {
  const router = useRouter();
  const pageLimit = 12;
  const {locale, defaultLocale} = useRouter();
  const [loading, setLoading] = useState<boolean>();
  const [videoCount, setVideoCount] = useState<number>(paragraph?.meta?.count ?? 0);
  const [page, setPage] = useState<number>(0);
  const widgetRef = useRef<HTMLDivElement|null>(null);
  const [activeFilters, setActiveFilters] = useState({} as FacetFilters);
  const [activeVideos, setActiveVideos] = useState<DrupalVideoMedia[]>(paragraph?.training_videos ?? []);
  const [activeMeta, setActiveMeta] = useState<DrupalSearchApiResponseMeta>(paragraph?.meta ?? {});
  const searchTextRef = useRef<HTMLInputElement | null>(null);
  const activeCategory = getActiveCategory();
  const hideFacets: string[] = ["media_product_reference"];
  const productFacet: DrupalSearchApiFacet|null = paragraph?.meta?.facets?.filter(item => item?.id === "media_product_reference")?.[0] ?? null;
  const t = useContext<Dictionary>(DictionaryContext);
  const totalVideos = activeMeta?.count ?? 0;

  useEffect(() => {
    for (const q of ["fulltext", "field_video_categories"]) {
      if (router.query?.[q] && !activeFilters?.[q]) {
        updateFilters(q, router?.query[q]?.toString() ?? "");
      }
    }
  }, [router?.query]);

  if (!paragraph?.training_videos?.length) return <></>

  // Create a dummy facet for the fulltext search field (which is not a facet).
  const titleFacet = {id: "fulltext", path: "fulltext", terms: [], label: t?.video_library?.search_videos};

  /**
   * Scrolls back to the top of the document search.
   *
   * @todo this is used in a few places, refactor it into a reusable function.
   */
  const scrollToRef = () => {
    if (!widgetRef?.current) return;
    const current = widgetRef.current;
    current.scrollIntoView({behavior: 'smooth'})
  }

  /**
   * Performs the video search.
   *
   * @param activeFilters
   * @param pageLimit
   * @param page
   */
  const searchVideos = async (activeFilters: FacetFilters, pageLimit: number, page: number) => {
    const pageOffset = Math.ceil(pageLimit * page);
    const result = await videoSearch(activeFilters, pageLimit, pageOffset, locale, defaultLocale);
    setActiveVideos(result?.data?.videos ?? []);
    setActiveMeta(result?.data?.meta ?? {});
    setVideoCount(result?.data?.meta?.count ?? 0);
    setLoading(false);
  }

  /**
   * Updates the active filters and performs a new faceted search.
   *
   * @param key
   * @param val
   */
  const updateFilters = async (key: string, val: string) => {
    if (!val || val === "0") {
      delete activeFilters[key];
    }
    else {
      activeFilters[key] = val;
    }
    const path = router.asPath.split("?")[0];
    await router.replace(
      {pathname: path, query: activeFilters},
      undefined,
      {
        shallow: true,
      },
    );
    await setLoading(true);
    await setActiveFilters(activeFilters);
    await setPage(0);
    await searchVideos(activeFilters, pageLimit, 0);
  }


  /**
   * Changes the current page.
   *
   * @param page
   */
  const changePage = async (page) => {
    await setLoading(true);
    scrollToRef();
    await setPage(page);
    await searchVideos(activeFilters, pageLimit, page);
  }

  /**
   * Resets the filters and re-fetches.
   */
  const resetFilters = async () => {
    if (searchTextRef?.current) {
      searchTextRef.current.value = '';
    }
    await setLoading(true);
    await setActiveFilters({});
    await setPage(0);
    await searchVideos({}, pageLimit, 0);
  }

  /**
   * Gets the actively selected category.
   */
  function getActiveCategory(): string {
    if (paragraph?.meta?.facets?.length) {
      const categoryFacet = paragraph?.meta?.facets?.["filter"](item => item?.id === "video_categories")?.[0];
      const activeItem = categoryFacet?.terms?.filter(term => {
        return term?.values?.value === activeFilters?.field_video_categories;
      })?.[0];
      return activeItem?.values?.label ?? "";
    }
    return '';
  }

  return (
    <div className="paragraph--solr-video-grid scroll-mt-24" ref={widgetRef}>
      <LoadingSpinnerOverlay loading={loading} fixed={true}/>
      <div className="flex flex-col md:flex-row md:py-6 gap-3 text-black items-center">
        {titleFacet && (
          <DocumentSearchFacet inputClassName="lg:min-w-[400px] !md:max-w-[500px] max-md:w-full" placeholder={t?.video_library?.search_placeholder}
                               autocompleteFacet={productFacet}
                               loading={loading}
                               key={titleFacet.id} facet={titleFacet} activeFilters={activeFilters}
                               setFilters={updateFilters} preFilter={[]} type="text" facetRef={searchTextRef} />
        )}
        {paragraph?.meta?.facets && paragraph?.meta.facets.map(facet => {
          let selectText = t?.documents?.select;
          if (facet?.id === "video_categories") {
            facet.label = t?.documents?.category;
          }
          if (!hideFacets.includes(facet?.id)) {
            const compareFacet = activeMeta?.facets?.filter(item => item?.id === facet?.id)?.[0];
            return <DocumentSearchFacet className="md:w-[320px] md:max-w-none" inputClassName="md:w-[320px]" key={facet.id} facet={facet} activeFilters={activeFilters} setFilters={updateFilters} preFilter={[]} selectText={selectText} compareFacet={compareFacet} />
          }
          return <Fragment key={facet.id}/>
        })}
        <FilterResetButton callback={resetFilters} enabled={!!Object.keys(activeFilters).length} className="self-end mb-2"/>
      </div>
      <h3 className={classNames("font-semibold pt-4 mb-4", {"max-md:hidden": !activeCategory})}>{activeCategory}</h3>
      <Body bodyType="lg" className="text-center py-6">{totalVideos} { totalVideos === 1 ? `${t?.video_library?.video_available}` : `${t?.video_library?.videos_available}` }{activeCategory && ` ${t?.video_library?.in_category} ${activeCategory}`}</Body>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {activeVideos.map((item: DrupalVideoMedia, key: number) => (
          <Link href={item?.langcode != defaultLocale ? `/${item.langcode}${item.path.alias}` : item.path.alias} title={item.name} key={`${paragraph.id}--${item.name}--${key}`}>
            <MediaVideoTeaser video={item} key={`paragraph-solr-video-grid--${paragraph.id}--${key}`} />
          </Link>
        ))}
      </div>
      <Pagination count={videoCount} page={page} limit={pageLimit} changePage={changePage} radius={5} />
    </div>
  )

}